import { Check, Clock3, Equal, MapPin, Minus, Plus, RotateCcw, Sparkles } from 'lucide-react';
import type { AiPreviewScreen } from '@/features/campaigns/types/campaign';
import { formatHourRange } from '@/features/campaigns/utils/campaignUtils';
import { formatMoney } from '@/shared/lib/format/money';
import { screenReason } from '../aiPreviewHelpers';
import { aiScreenKey, perPlayPrice } from '../aiPriceEstimate';

type ScreenPlansListProps = {
  screens: AiPreviewScreen[];
  selectedKeys: Set<string>;
  playsByKey: Record<string, number>;
  maxPlaysByKey: Record<string, number>;
  /** Hours each screen plays, by screen id. */
  hoursById: Record<string, number[]>;
  days: number;
  /** Dimmed while the plan is being rebuilt. */
  busy: boolean;
  isCustomised: boolean;
  onToggle: (key: string) => void;
  onPlaysChange: (key: string, plays: number) => void;
  onReset: () => void;
  /** Budget left after the current plan, and a way to spend it on extra plays. */
  remainingBudget: number | null;
  canFillBudget: boolean;
  onFillBudget: () => void;
  /** Give every chosen screen the same plays a day. */
  onEqualPlays: () => void;
};

export function ScreenPlansList({
  screens,
  selectedKeys,
  playsByKey,
  maxPlaysByKey,
  hoursById,
  days,
  busy,
  isCustomised,
  onToggle,
  onPlaysChange,
  onReset,
  remainingBudget,
  canFillBudget,
  onFillBudget,
  onEqualPlays,
}: ScreenPlansListProps) {
  const selectedCount = screens.filter((s) => selectedKeys.has(aiScreenKey(s))).length;
  const totalPlays = screens.reduce(
    (sum, s) => sum + (selectedKeys.has(aiScreenKey(s)) ? (playsByKey[aiScreenKey(s)] ?? 0) : 0),
    0,
  );

  return (
    <section className="camp-section text-left" aria-busy={busy}>
      <div className="mb-1 flex items-start justify-between gap-3">
        <div>
          <h2 className="m-0 text-[15px] font-semibold text-[var(--dash-ink)]">Your screens and plays</h2>
          <p className="mt-1 mb-0 text-xs text-[var(--dash-muted)]">
            AI picked these to fit your budget. Untick a screen or change how often your ad plays each day. The price updates as you go.
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {selectedCount > 1 ? (
            <button
              type="button"
              onClick={onEqualPlays}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-lg border border-[var(--dash-brand)]/40 px-2.5 py-1.5 text-xs font-semibold text-[var(--dash-brand)] disabled:opacity-50"
            >
              <Equal className="h-3.5 w-3.5" />
              Equal plays on all screens
            </button>
          ) : null}
          {isCustomised ? (
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--dash-brand)] hover:underline"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset
            </button>
          ) : null}
        </div>
      </div>

      {screens.length === 0 ? (
        <p className="mt-4 mb-0 text-sm text-[var(--dash-muted)]">No screens selected by AI.</p>
      ) : (
        <ul className={`m-0 mt-4 flex list-none flex-col gap-3 p-0 transition-opacity ${busy ? 'opacity-50' : ''}`}>
          {screens.map((screen) => {
            const key = aiScreenKey(screen);
            const selected = selectedKeys.has(key);
            const plays = playsByKey[key] ?? 0;
            const maxPlays = maxPlaysByKey[key] ?? plays;
            const place = [screen.address?.area, screen.address?.city].filter(Boolean).join(', ');
            const lineCost = perPlayPrice(screen).gross * plays * days;
            const hours = hoursById[String(screen._id)] ?? [];
            const labelId = `screen-${key}`;

            return (
              <li
                key={key}
                className={`rounded-2xl border bg-white p-3.5 transition ${
                  selected ? 'border-[var(--dash-brand)]/50' : 'border-[var(--dash-line)] opacity-70'
                }`}
              >
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={selected}
                    aria-labelledby={labelId}
                    onClick={() => onToggle(key)}
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                      selected
                        ? 'border-[var(--dash-brand)] bg-[var(--dash-brand)] text-white'
                        : 'border-[var(--dash-line)] text-transparent'
                    }`}
                  >
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </button>

                  <div className="min-w-0 flex-1">
                    <p id={labelId} className="m-0 flex min-w-0 flex-wrap items-center gap-x-2 text-sm font-bold text-[var(--dash-ink)]">
                      <span className="line-clamp-2 min-w-0 break-words [overflow-wrap:anywhere]" title={screen.screenName}>
                        {screen.screenName || 'Screen'}
                      </span>
                      {screen.addedByUser ? (
                        <span className="rounded-full bg-[var(--dash-accent-soft)] px-2 py-0.5 text-[10px] font-bold text-[var(--dash-accent)]">
                          Added by you
                        </span>
                      ) : null}
                    </p>
                    <p className="m-0 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-[var(--dash-muted)]">
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3 w-3 shrink-0" />
                        {place || '—'}
                      </span>
                      {hours.length ? (
                        <span className="inline-flex items-center gap-1">
                          <Clock3 className="h-3 w-3 shrink-0" />
                          {hours.map((h) => formatHourRange(h)).join(', ')}
                        </span>
                      ) : null}
                    </p>
                    <p className="m-0 mt-2 flex items-start gap-1.5 text-xs leading-relaxed text-[var(--dash-ink)]">
                      <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-[var(--dash-brand)]" />
                      <span className="line-clamp-2 min-w-0 break-words [overflow-wrap:anywhere]">{screenReason(screen)}</span>
                    </p>
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    <div className="flex items-center gap-1.5" role="group" aria-label={`Plays per day for ${screen.screenName}`}>
                      <button
                        type="button"
                        aria-label="Fewer plays per day"
                        disabled={!selected || plays <= 1}
                        onClick={() => onPlaysChange(key, plays - 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-lg border border-[var(--dash-line)] text-[var(--dash-ink)] disabled:opacity-40"
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="min-w-[2.25rem] text-center text-sm font-bold text-[var(--dash-ink)]" aria-live="polite">
                        {selected ? plays : 0}
                      </span>
                      <button
                        type="button"
                        aria-label="More plays per day"
                        disabled={!selected || plays >= maxPlays}
                        onClick={() => onPlaysChange(key, plays + 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-lg border border-[var(--dash-line)] text-[var(--dash-ink)] disabled:opacity-40"
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <span className="text-[10px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">plays / day</span>
                    <span className="text-xs font-semibold text-[var(--dash-ink)]">
                      {selected ? formatMoney(lineCost) : '—'}
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {screens.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="m-0 text-xs text-[var(--dash-muted)]">
            {totalPlays} plays a day over {days} day{days === 1 ? '' : 's'} · {totalPlays * days} plays in total
          </p>
          {remainingBudget != null && remainingBudget > 0.5 ? (
            canFillBudget ? (
              <button
                type="button"
                onClick={onFillBudget}
                disabled={busy}
                className="rounded-lg border border-[var(--dash-brand)]/40 bg-[var(--dash-brand-soft)] px-3 py-1.5 text-xs font-semibold text-[var(--dash-brand)] disabled:opacity-50"
              >
                Use remaining {formatMoney(remainingBudget)}
              </button>
            ) : (
              <span className="text-xs text-[var(--dash-muted)]">{formatMoney(remainingBudget)} left in your budget</span>
            )
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
